import PropTypes from 'prop-types';
import React from 'react';
import { createPortal } from 'react-dom';

import DateField from 'src/components/DateField';

import {
  DISQUALIFICATION_REASON_OPTIONS,
  subDisqualificationReasonsFor,
} from '../disqualificationOptions';
import styles from './DisqualifyLeadModal.module.scss';

const DisqualifyLeadModal = ({
  leadName = 'this lead',
  lead,
  onClose,
  onConfirm,
  isSubmitting = false,
}) => {
  const [form, setForm] = React.useState({
    disqualificationReason: lead?.disqualificationReason || '',
    subDisqualificationReason: lead?.subDisqualificationReason || '',
    disqualifiedDate: lead?.disqualifiedDate || '',
    disqualifiedTime: `${lead?.disqualifiedTime || ''}`.slice(0, 5),
  });
  const [showValidation, setShowValidation] = React.useState(false);

  React.useEffect(() => {
    setForm({
      disqualificationReason: lead?.disqualificationReason || '',
      subDisqualificationReason: lead?.subDisqualificationReason || '',
      disqualifiedDate: lead?.disqualifiedDate || '',
      disqualifiedTime: `${lead?.disqualifiedTime || ''}`.slice(0, 5),
    });
    setShowValidation(false);
  }, [lead]);

  React.useEffect(() => {
    const handleEscape = (event) => {
      if (event.key !== 'Escape' || isSubmitting) return;
      onClose?.();
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isSubmitting, onClose]);

  const handleConfirm = async () => {
    if (!form.disqualificationReason || !form.disqualifiedDate) {
      setShowValidation(true);
      return;
    }

    const wasDisqualified =
      (await onConfirm?.({
        disqualificationReason: form.disqualificationReason || null,
        subDisqualificationReason: form.subDisqualificationReason || null,
        disqualifiedDate: form.disqualifiedDate || null,
        disqualifiedTime: form.disqualifiedTime || null,
      })) ?? false;
    if (!wasDisqualified) return;
    onClose?.();
  };

  const modalContent = (
    <div className={styles.modalOverlay}>
      <button
        type="button"
        className={styles.modalBackdrop}
        aria-label="Close dialog"
        onClick={() => !isSubmitting && onClose?.()}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="disqualify-lead-title"
        className={styles.modalCard}
      >
        <div className={styles.modalHeader}>
          <div className={styles.modalTitle} id="disqualify-lead-title">
            Disqualify this lead?
          </div>
        </div>

        <div className={styles.modalBody}>
          <p className={styles.modalCopy}>
            <span className={styles.modalCopyStrong}>{leadName}</span> will be marked as
            disqualified. This closes the lead&apos;s intake workflow and cancels its remaining
            tasks; no consent form or patient record will be created. This cannot be undone from
            here.
          </p>
          <div className={styles.formGrid}>
            <label className={styles.field} htmlFor="disqualify-reason">
              <span>
                Disqualification Reason <em className={styles.required}>Required</em>
              </span>
              <select
                id="disqualify-reason"
                value={form.disqualificationReason}
                aria-invalid={showValidation && !form.disqualificationReason}
                onChange={(event) => {
                  const reason = event.target.value;
                  setForm((current) => ({
                    ...current,
                    disqualificationReason: reason,
                    subDisqualificationReason: subDisqualificationReasonsFor(reason).includes(
                      current.subDisqualificationReason,
                    )
                      ? current.subDisqualificationReason
                      : '',
                  }));
                }}
              >
                <option value="">--None--</option>
                {DISQUALIFICATION_REASON_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {showValidation && !form.disqualificationReason ? (
                <em className={styles.validationError}>Select a disqualification reason.</em>
              ) : null}
            </label>
            <label className={styles.field} htmlFor="disqualify-sub-reason">
              <span>Sub Disqualification Reason</span>
              <select
                id="disqualify-sub-reason"
                value={form.subDisqualificationReason}
                disabled={!subDisqualificationReasonsFor(form.disqualificationReason).length}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    subDisqualificationReason: event.target.value,
                  }))
                }
              >
                <option value="">--None--</option>
                {subDisqualificationReasonsFor(form.disqualificationReason).map((reason) => (
                  <option key={reason} value={reason}>
                    {reason}
                  </option>
                ))}
              </select>
            </label>
            <DateField
              id="disqualify-date"
              label="Disqualified Date *"
              value={form.disqualifiedDate}
              errorText={
                showValidation && !form.disqualifiedDate ? 'Enter a disqualified date.' : ''
              }
              onChange={(value) => setForm((current) => ({ ...current, disqualifiedDate: value }))}
            />
            <label className={styles.field} htmlFor="disqualify-time">
              <span>Disqualified Time</span>
              <input
                id="disqualify-time"
                type="time"
                value={form.disqualifiedTime}
                onChange={(event) =>
                  setForm((current) => ({ ...current, disqualifiedTime: event.target.value }))
                }
              />
            </label>
          </div>
        </div>

        <div className={styles.modalActions}>
          <button
            type="button"
            className={`${styles.modalActionButton} ${styles.modalSecondaryAction}`}
            onClick={() => onClose?.()}
            disabled={isSubmitting}
          >
            Keep lead
          </button>
          <button
            type="button"
            className={`${styles.modalActionButton} ${styles.modalDangerAction}`}
            onClick={handleConfirm}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Disqualifying...' : 'Disqualify lead'}
          </button>
        </div>
      </div>
    </div>
  );

  if (typeof document === 'undefined') return modalContent;

  return createPortal(modalContent, document.body);
};

DisqualifyLeadModal.propTypes = {
  leadName: PropTypes.string,
  lead: PropTypes.shape({
    disqualificationReason: PropTypes.string,
    subDisqualificationReason: PropTypes.string,
    disqualifiedDate: PropTypes.string,
    disqualifiedTime: PropTypes.string,
  }),
  onClose: PropTypes.func,
  onConfirm: PropTypes.func,
  isSubmitting: PropTypes.bool,
};

export default DisqualifyLeadModal;
